import './App.scss';
import './mediaQuery.scss';
import '@masterffl/ffl-grid-v2-component/dist/bundle.css';
// Add this import at the top of your component or in your main app file
import 'flatpickr/dist/flatpickr.min.css';
// Or use a themed version
import 'flatpickr/dist/themes/material_blue.css';
import './Flatpickr.scss';
import './map-dealer-card.css';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  createBrowserRouter,
  RouterProvider,
  useNavigate
} from 'react-router-dom';
import Outer from '@/layout/Outer';
import Queue from '@/queue/Queue';
import Explore from '@/explore/Explore';
import ExploreV2 from '@/explore/Explore-v2';
import { Amplify } from 'aws-amplify';
import AppConfig, { DynamicAmplifyConfiguration } from './utils/app-config.mjs';
import getAccessTokenIstance from './utils/authentication';
import {
  setIsStoreAdmin,
  setIsGlobalAdmin,
  setStoreId,
  setUserName,
  setIsImpersonating,
  setIsUserInfoLoaded,
  setIsGlobalAdminRole,
  setIsMyFFLRole,
  setMyFFLDealerId,
  setIsGlobalOperationsAdmin,
  setIsGlobalOperationsAdminRole,
  setIsStoreAgent,
  setIsPreferredReportsRole,
  setCurrentUserGroup,
  setIsSearchOnlyRole,
  setUserId,
  setUserEmail,
  setIsStoreAdminRole,
  setStoreDealerID,
  setUserPermissions,
  callToGetTrackingData
} from './redux/app-slice';
import Review from './review';
import ProtectedRoute from './routes/ProtectedRoute';
import LicenseDetailsPage from './explore/ffl-detail/license-details-page';
import StoreAdmin from './storeAdmin';
import { getIsImpersonating, getIsUserInfo, getStoreData, getUserEmail,getIsStoreAgent, getIsStoreAdminRole  } from './redux/app-selectors';
import DealerProfile from '@/dealerProfile';
import TransferDetailsV2 from './queue/elements/transfer-details-v2';
import TagManager from 'react-gtm-module';
import { logAnalyticsEvent } from './utils/analyticsLogger';
import { analyticsEvents } from './utils/analyticsEvents';
import { getAdminTabPath, getHideSubTabs, getHideTabs } from './utils/utils';
import { Box, Spinner } from '@chakra-ui/react';
import LicenseApprovalPage from './review/components/licenseApprovalPage';
import SOTApprovalPage from './review/components/SOTApprovalPage';
import LOAApprovalPage from './review/components/LOAApprovalPage';
import { getCurrentUser, fetchAuthSession, signInWithRedirect } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import CheckoutDocuments from './checkout/CheckoutDocuments';
import DealerLogs from './dealer-logs/dealer-logs';

const HomePage = ({ isGlobalOperations, isPreferredReportsRole, isSearchOnlyRole, hideTabs, isStoreAdmin, isStoreAgent, userPermissions }: { isGlobalOperations: boolean; isPreferredReportsRole: boolean; isSearchOnlyRole: boolean; hideTabs: any[]; isStoreAdmin: boolean; isStoreAgent: boolean; userPermissions: any; }) => {
  const navigate = useNavigate();
  const [pageLoading, setPageLoading] = useState(true)
  const [isAgentNoAnyPermission, setIsAgentNoAnyPermission] = useState(false)
  const isImpersonating = getIsImpersonating();

  useEffect(() => {
    if ((isStoreAdmin || isStoreAgent) && !isImpersonating) {
      if (hideTabs?.length > 0 || (userPermissions && Object.keys(userPermissions)?.length > 0)) {
        setIsAgentNoAnyPermission(false)
        if (!hideTabs.includes('transfer')) {
          navigate('/transfers');
        } else if (!hideTabs.includes('dealerSearch')) {
          navigate('/dealers-search');
        } else if (isStoreAdmin && !hideTabs.includes('admin')) {
          const subTabs = getHideSubTabs(isStoreAdmin, isStoreAgent, userPermissions)
          const path = getAdminTabPath(subTabs)
          navigate(path);
        }
        if (isStoreAgent && hideTabs?.length > 0 && hideTabs.includes('transfer') && hideTabs.includes('dealerSearch')) {
          setIsAgentNoAnyPermission(true)
        }
        setPageLoading(false)
      } else {
        if (userPermissions && Object.keys(userPermissions)?.length > 0) {
          setPageLoading(false)
        }else{
          setPageLoading(true)
        }
        
      }
    } else if ((isPreferredReportsRole || isSearchOnlyRole)) {
      navigate('/dealers-search');
    } else {
      navigate('/transfers');
    }
  });

  const getPageLoading = () => {
    if (isStoreAdmin || isStoreAgent) {
      if (isAgentNoAnyPermission) {
        return <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <p>You do not have any permission to access this page.</p>
          <p>Please contact MasterFFL customer care.</p>
        </Box>
      }
      return <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Spinner
          className={`preview-loader`}
          thickness={'4px'}
          speed="0.65s"
          emptyColor={'gray.200'}
          color="#0C1E46"
          size={'xl'}
        />
      </Box>
    }
    return null;
  }
  return pageLoading || isAgentNoAnyPermission ? getPageLoading() : null;
};

const MyFFLHomePage = (props: { id: string }) => {
  const navigate = useNavigate();
  useEffect(() => {
    navigate(`/dealers/${props.id}`);
  }, [navigate, props.id]);
  return null;
};

const router = (
  group: boolean,
  isSuperAdmin: boolean,
  isGlobalOperations: boolean,
  isStoreAdmin: boolean,
  isStoreAgent: boolean,
  isGettingUserInfo: boolean,
  isSuperAdminRole: boolean,
  isPreferredReportsRole: boolean,
  isSearchOnlyRole: boolean,
  isImpersonating: boolean,
  hideTabs: any[],
  userPermissions: any
) =>
  createBrowserRouter([
    {
      path: '/',
      element: <Outer />,
      children: [
        {
          index: true,
          element: <HomePage isGlobalOperations={isGlobalOperations}
            isPreferredReportsRole={isPreferredReportsRole}
            isSearchOnlyRole={isSearchOnlyRole}
            hideTabs={hideTabs}
            isStoreAdmin={isStoreAdmin}
            isStoreAgent={isStoreAgent}
            userPermissions={userPermissions}
          />
        },
        ...((
          isPreferredReportsRole ||
          isSearchOnlyRole || hideTabs?.includes('transfer')) ? [] : [
          {
            path: 'transfers',
            element: (
              <Queue
                cognitoGroup={group}
                isSuperAdmin={isSuperAdmin}
                isStoreAdmin={isStoreAdmin}
                isStoreAgent={isStoreAgent}
              />
            )
          }
        ]),
        ...(hideTabs?.includes('dealerSearch') ? [] : [
          {
            path: 'dealers',
            element: (
              <Explore
                cognitoGroup={group}
                isSuperAdmin={isSuperAdmin}
                isStoreAdmin={isStoreAdmin}
                isStoreAgent={isStoreAgent}
              />
            )
          },
          {
            path: 'dealers-search/:dealerId',
            element:

              <DealerProfile isSuperAdmin={isSuperAdmin} userPermissions={userPermissions}/>

          },
          {
            path: 'dealers-search',
            element: (

              <ExploreV2
                cognitoGroup={group}
                isSuperAdmin={isSuperAdmin}
                isStoreAdmin={isStoreAdmin}
              />
            )
          },
        ]),
        ...(hideTabs?.includes('admin') ? [] : [
          {
            path: 'admin/license-approval/:id',
            element:
              <ProtectedRoute
                isGettingUserInfo={isGettingUserInfo}
                isAllowed={isSuperAdmin || isGlobalOperations}
              >
                <LicenseApprovalPage />
            </ProtectedRoute>
          },
          {
            path: 'admin/sot-tax-stamps/:id',
            element:
              <ProtectedRoute
                isGettingUserInfo={isGettingUserInfo}
                isAllowed={isSuperAdmin || isGlobalOperations}
              >
                <SOTApprovalPage />
              </ProtectedRoute>
          },
          {
            path: 'admin/loa-approval/:id',
            element:
              <ProtectedRoute
                isGettingUserInfo={isGettingUserInfo}
                isAllowed={isSuperAdmin || isGlobalOperations}
              >
                <LOAApprovalPage />
            </ProtectedRoute>
          },
          {
            path: '/admin/:tabName',
            element: (isStoreAdmin || isPreferredReportsRole || isSearchOnlyRole) ? (
              <ProtectedRoute
                isGettingUserInfo={isGettingUserInfo}
                isAllowed={isStoreAdmin || isPreferredReportsRole || isSearchOnlyRole}
              >
                <StoreAdmin userPermissions={userPermissions} />
              </ProtectedRoute>
            ) : (
              <ProtectedRoute
                isGettingUserInfo={isGettingUserInfo}
                isAllowed={isSuperAdmin || isGlobalOperations}
              >
                <Review />
              </ProtectedRoute>
            )
          }
        ]),
        {
          path: 'license-details/:first3/:last5',
          element: (
            <LicenseDetailsPage
              isSuperAdmin={isSuperAdmin}
            ></LicenseDetailsPage>
          )
        },
        ...(hideTabs?.includes('transfer') ? [] : [
          {
            path: '/transfers/:transferId',
            element: (
              <TransferDetailsV2
                isGlobalAdmin={isSuperAdmin}
                isStoreAdmin={isStoreAdmin}
                isSuperAdmin={isSuperAdmin}
                isStoreAgent={isStoreAgent}
                isGlobalOperations={isGlobalOperations}
                >
              </TransferDetailsV2>
            )
          },
        ]),
        {
          path: 'activity-logs',
          element: (
            <ProtectedRoute
              isGettingUserInfo={isGettingUserInfo}
              isAllowed={isSuperAdmin || isGlobalOperations}
            >
              <DealerLogs />
            </ProtectedRoute>
          )
        },
        {
          path: '*',
          element: <HomePage
            isGlobalOperations={isGlobalOperations}
            isPreferredReportsRole={isPreferredReportsRole}
            isSearchOnlyRole={isSearchOnlyRole}
            hideTabs={hideTabs}
            isStoreAdmin={isStoreAdmin}
            isStoreAgent={isStoreAgent}
            userPermissions={userPermissions}
          />
        }
      ]
    }
  ]);

const myFFLRouter = (
  isSuperAdmin: boolean,
  fFLDealerId: string,
  userPermissions: any,
  claimedProfiles:any[]
) =>
  createBrowserRouter([
    {
      path: '/',
      element: <Outer />,
      children: [
        {
          path: 'dealers/:dealerId',
          element: <DealerProfile isSuperAdmin={isSuperAdmin} userPermissions={userPermissions} />
        },
        {
          path: '*',
          element: <MyFFLHomePage id={fFLDealerId} />
        },
        {
          path: '',
          element: <MyFFLHomePage id={fFLDealerId} />, // Explicit root route
        },
      ]
    }
  ]);

// Create a public router for checkout routes
const createPublicRouter = () =>
  createBrowserRouter([
    {
      path: '/checkout/documents/ffl',
      element: <CheckoutDocuments />
    },
    {
      path: '*',
      element: <div>Page not found</div>
    }
  ]);

function App() {
  const hasSetupOnShow = useRef(false);
  const clearAuthStorage = () => {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.includes('CognitoIdentityServiceProvider') ||
        key.includes('amplify') ||
        key.includes('aws')
      )) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    const sessionKeysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && (
        key.includes('CognitoIdentityServiceProvider') ||
        key.includes('amplify') ||
        key.includes('aws')
      )) {
        sessionKeysToRemove.push(key);
      }
    }
    sessionKeysToRemove.forEach(key => sessionStorage.removeItem(key));
  };
  const getClientType = () => {
    const currentPath = window.location.pathname;
    if (currentPath.includes("automationLogin")) {
      const currentClientType = localStorage.getItem('clientType');
      if (currentClientType !== 'automation') {
        console.log('Switching to automation client, clearing auth storage');
        clearAuthStorage();
        localStorage.setItem('clientType', 'automation');
      }
      return 'automation';
    }
    const storedClientType = localStorage.getItem('clientType');
    return storedClientType || "regular"
  };

  const [clientType, setClientType] = useState(() => getClientType());  
  const isAutomation = clientType === 'automation';
  
  
  const [amplifyConfigured, setAmplifyConfigured] = useState(false);
  useEffect(() => {
    const correctClientType = getClientType();
    if (correctClientType !== clientType) {
      setClientType(correctClientType);
      setAmplifyConfigured(false);
    }
  }, []);
  
  useEffect(() => {
    if (!amplifyConfigured) {
      try {
        const config = DynamicAmplifyConfiguration(isAutomation);
        Amplify.configure(config);
        setAmplifyConfigured(true);
      } catch (error) {
        console.error('Error configuring Amplify:', error);
      }
    }
  }, [amplifyConfigured, isAutomation, clientType]);
  
  // vars
  const dispatch = useDispatch();
  
  // vars
  // global state
  const isGettingUserInfo = getIsUserInfo();
  // local state
  const [user, setUser] = useState<any>({});
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false);
  const [isGlobalOperations, setIsGlobalOperations] = useState<boolean>(false);
  const [isSuperAdminRole, setIsSuperAdminRole] = useState<boolean>(false);
  const [isSuperOperationsRole, setIsSuperOperationsRole] = useState<boolean>(false);
  const [isStoreAdmin, setLocalStoreAdmin] = useState<boolean>(false);
  const [isStoreAgent, setLocalStoreAgent] = useState<boolean>(false);
  const [isMyFFLRole, setMyFFLRole] = useState<boolean>(false);
  const [fFLDealerId, setFFLDealerId] = useState<string>('');
  const [group, setGroup] = useState<boolean>(false);
  const [isPreferredReportRole, setIsPreferredReportRole] = useState<boolean>(false);
  const [isSearchOnlyAdminRole, setIsSearchOnlyAdminRole] = useState<boolean>(false);
  const [isImpersonatingRole, setIsImpersonatingRole] = useState<boolean>(false);
  const [claimedProfilesData, setClaimedProfilesData] = useState<any[]>([]);
  const storeData = getStoreData()
  const isInitialized = useRef(false);
  const userEmail = getUserEmail();
   const IsStoreAdminRole = getIsStoreAdminRole();
  const IsStoreAgent = getIsStoreAgent();
  console.log({storeData, userEmail})


  const [userPermissions, setUserPermissionsLocalState] = useState({})


   useEffect(() => {
    const handler = (event: any) => {

      let el = event.target;
      if(storeData?.is_external && (IsStoreAdminRole || IsStoreAgent)) {
      dispatch(callToGetTrackingData({
            type: "total_clicks",
            time: new Date(),
            ...(storeData?.store_id ? {storeId: storeData?.store_id} : {}),
            email: userEmail || ''
         }))
      while (el && el !== document) {
        const eventType = el.getAttribute?.("data-referrer");
        console.log("Clicked element eventType: ", eventType)
       
        if (eventType) {
         dispatch(callToGetTrackingData({
            type: eventType,
            time: new Date(),
            ...(storeData?.store_id ? {storeId: storeData?.store_id} : {}),
            email: userEmail || ''
         }))
          break;
        }
        el = el.parentElement;
      }
    }
    };
 
    // Attach once on first render
    document.addEventListener("click", handler, {capture: true});
 
    // Cleanup on unload
    return () => {
      document.removeEventListener("click", handler, {capture: true});
    };
  }, [userEmail, storeData?.store_id]); // runs only once

  // Check if current path is a public route
  const isPublicRoute = () => {
    const currentPath = window.location.pathname;
    return currentPath.startsWith('/checkout/documents/ffl');
  };

  const checkAuthStatus = async () => {
    console.log("Checking auth status")
    try {
      const currentUser = await getCurrentUser();
      let session;
      try {
        session = await fetchAuthSession();
      } catch (sessionError: any) {
        console.error("Session fetch error:", sessionError);
        if (sessionError?.message?.includes('400') || sessionError?.statusCode === 400) {
          clearAuthStorage();
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }
        throw sessionError;
      }

      if (currentUser && session?.tokens) {
        setUser({...currentUser, attributes: session?.tokens?.idToken?.payload});
        setIsAuthenticated(true);
        console.time("start")
        await initializeUserData();
        console.timeEnd("start")
      } else {
        console.log("No valid user or session");
        setIsAuthenticated(false);
      }
    } catch (error: any) {
      console.error('Auth check error:', error);
      
      if (error?.name === 'UserUnAuthenticatedException' || 
          error?.message?.includes('User needs to be authenticated')) {
        setIsAuthenticated(false);
      } else if (error?.name === 'NotAuthorizedException') {
        clearAuthStorage();
        setIsAuthenticated(false);
      } else {
        console.log("Unknown auth error, clearing storage");
        clearAuthStorage();
        setIsAuthenticated(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const setupOnShowTracking = () => {
        if (hasSetupOnShow.current || !window?.Intercom) return;
        hasSetupOnShow.current = true;
        window.Intercom('onShow', () => {
            window.Intercom('update', {
              current_page: window.location.href,
              referrer: window.location.href,
              updated_at: Math.floor(Date.now() / 1000)
            } as any);
        });
  };


  function setIntercomConfig(userEmail?: string, userId?: string, userName?: string, storeDealerId?: string) {
    var APP_ID = AppConfig.intercomAppId;
     isInitialized.current = true;
    if (APP_ID) {
      // @ts-ignore
      (function () { var w = window; var ic = w.Intercom; if (typeof ic === "function") { ic('reattach_activator'); ic('update', w.intercomSettings); } else { var d = document; var i = function () { i.c(arguments); }; i.q = []; i.c = function (args) { i.q.push(args); }; w.Intercom = i; var l = function () { var s = d.createElement('script'); s.type = 'text/javascript'; s.async = true; s.src = 'https://widget.intercom.io/widget/' + APP_ID; var x = d.getElementsByTagName('script')[0]; x.parentNode.insertBefore(s, x); }; if (document.readyState === 'complete') { l(); } else if (w.attachEvent) { w.attachEvent('onload', l); } else { w.addEventListener('load', l, false); } } })();
      if (window?.Intercom) {
        const intercomAttributes = {
          app_id: APP_ID,
          email: userEmail,
          user_id: userId,
          name: userName,
          created_at: Math.floor(Date.now() / 1000),
          current_page: window.location.href,
          referrer: window.location.href,
          utm_source: window.location.hostname.replace('www', ''),
          ffl360_link: storeDealerId && Object.keys(storeDealerId)?.length ? `${window.location.origin}/dealer-search/${storeDealerId}` : null
        }
        console.log({intercomAttributes})
        window?.Intercom('boot', intercomAttributes);
        setupOnShowTracking()
      }
    }
  }

  function gtmHandler() {
    if (!!AppConfig.GMTId) {
      // GTM config
      const tagManagerArgs = {
        gtmId: AppConfig.GMTId as string,
        dataLayer: {
          storeId: user?.attributes?.["custom:storeId"],
          userId: user?.username
        }
      }
      TagManager.initialize(tagManagerArgs);
      console.log("GTM", { tagManagerArgs })
    }
  }

  async function decodeCognitoToken() {
    const accessTokenINstance = await getAccessTokenIstance();
    const isAdmin = accessTokenINstance.isStoreAdmin();
    const isSuperAdmin = accessTokenINstance.isGlobalAdmin();
    const isSuperAdminRole = accessTokenINstance.isGlobalAdminRole();
    const isGlobalOperations = accessTokenINstance.isGlobalOperationsAdmin();
    const isSuperOperationsRole = accessTokenINstance.isGlobalOperationsRole();
    const isStoreAdmin = accessTokenINstance.isStoreAdmin();
    const isStoreAgentRole = accessTokenINstance.isStoreAgentRole();
    const userName = accessTokenINstance.getFullName();
    const userEmail = accessTokenINstance.getEmail() || "";
    const userId = accessTokenINstance.getUserId() || "";
    const isImpersonating = accessTokenINstance.isImpersonating();
    const isMyFFLRole = accessTokenINstance.isMyFFLRole();
    const isPreferredReportsRole = accessTokenINstance.isPreferredReportsRole();
    const isSearchOnlyRole = accessTokenINstance.isSearchOnlyRole();
    const currentUserGroup = accessTokenINstance.getGroup() || [''];
    const isStoreAdminRole = accessTokenINstance.isStoreAdminRole();
    const storeDealerId = accessTokenINstance.getStoreDealerId();
    const claimedProfiles = accessTokenINstance.getMyFFLClaimProfiles();
    

    dispatch(setUserName(userName));
    dispatch(setIsStoreAdmin(isStoreAdmin));
    dispatch(setIsStoreAdminRole(isStoreAdminRole));
    dispatch(setIsStoreAgent(isStoreAgentRole));
    dispatch(setIsGlobalAdmin(isSuperAdmin));
    dispatch(setIsGlobalOperationsAdmin(isGlobalOperations));
    dispatch(setIsGlobalAdminRole(isSuperAdminRole));
    dispatch(setIsGlobalOperationsAdminRole(isSuperOperationsRole));
    dispatch(setIsImpersonating(isImpersonating));
    dispatch(setIsMyFFLRole(isMyFFLRole));
    dispatch(setIsPreferredReportsRole(isPreferredReportsRole));
    dispatch(setIsSearchOnlyRole(isSearchOnlyRole));
    dispatch(setCurrentUserGroup(currentUserGroup[0]));
    dispatch(setUserId(userId))
    dispatch(setUserEmail(userEmail));
    dispatch(setStoreDealerID(storeDealerId));
    console.log({storeDealerId})

    if (isMyFFLRole) {
      const myFFLDealerId = accessTokenINstance.getDealerId();
      setFFLDealerId(myFFLDealerId || '');
      dispatch(setMyFFLDealerId(myFFLDealerId || ''));
    }

    if (!isSuperAdmin)
      dispatch(setStoreId(accessTokenINstance.getStoreId() || ''));

    setLocalStoreAdmin(isStoreAdmin);
    setIsSuperAdmin(isSuperAdmin);
    setIsGlobalOperations(isGlobalOperations)
    setIsSuperAdminRole(isSuperAdminRole);
    setLocalStoreAgent(isStoreAgentRole);
    setIsSuperOperationsRole(isSuperOperationsRole)
    setGroup(isAdmin);
    setMyFFLRole(isMyFFLRole);
    setIsPreferredReportRole(isPreferredReportsRole);
    setIsSearchOnlyAdminRole(isSearchOnlyRole);
    setIsImpersonatingRole(isImpersonating);

    setClaimedProfilesData(claimedProfiles)
    // intercom config
    setIntercomConfig(userEmail, userId, userName, storeDealerId);
    dispatch(setIsUserInfoLoaded(true));
  }

  const refreshCognitoSession = async () => {
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      return session;
    } catch (error) {
      console.error('Error refreshing session:', error);
      throw error;
    }
  };

  // const onSetIsOutbound = (savedUserPermissions: any, isImpersonating: boolean) => {
  //       // Only check permissions for agents and admins
        
  //       if ((isStoreAgent || isStoreAdmin) && !isImpersonating) {
  //         const savedIsOutbound = localStorage.getItem('savedIsOutbound') || '';
  //         if (savedUserPermissions?.transfer?.length > 0 && 
  //             savedUserPermissions?.transfer?.includes('inbound') && 
  //             savedUserPermissions?.transfer?.includes('outbound')) {
  //               if(savedIsOutbound === ''){
  //                 localStorage.setItem('savedIsOutbound', 'true');
  //               }
  //         } else if (savedUserPermissions?.transfer?.length > 0 && 
  //                    savedUserPermissions?.transfer?.includes('inbound')) {
  //           localStorage.setItem('savedIsOutbound', 'false');
  //         } else if(savedUserPermissions?.transfer?.length > 0 && 
  //                   savedUserPermissions?.transfer?.includes('outbound')){
  //           localStorage.setItem('savedIsOutbound', 'true');
  //         }
  //       }
  // }

  const onGetPermissions = async () => {
    try {
      await refreshCognitoSession();
      const accessTokenINstance = await getAccessTokenIstance();
      const userPermissions = accessTokenINstance.getUserPermissions()
      // const isImpersonating = accessTokenINstance.isImpersonating();
      setUserPermissionsLocalState(userPermissions);
      dispatch(setUserPermissions(userPermissions));
      // localStorage.setItem('savedUserPermissions', JSON.stringify(userPermissions));
      // onSetIsOutbound(userPermissions, isImpersonating);
    } catch (error) {
      console.error('Error getting permissions:', error);
    }
  }

  const initializeUserData = async () => {
    const currentPath = window.location.pathname;
    const isTransferDetailPage = /\/transfers\/\d+/.test(currentPath);

    gtmHandler();
    await decodeCognitoToken();
    if (!isTransferDetailPage) {
      await onGetPermissions();
    }
  };

  useEffect(() => {
    const hubListener = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signInWithRedirect':
          console.log('User signed in successfully');
          break;
        case 'customOAuthState':
          const returnPath = payload.data;
          console.log('Custom state received:', returnPath);
          
          if (returnPath && returnPath !== '/' && returnPath !== window.location.pathname) {
            console.log('Redirecting to:', returnPath);
            window.location.href = returnPath;
          }
          break;
      }
    });
        return () => hubListener();
  }, []);

  useEffect(() => {
    if (!amplifyConfigured) {
      return;
    }

    const handleOAuthCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const error = urlParams.get('error');
      
      if (error) {
        console.error('OAuth error:', error);
        clearAuthStorage();
        localStorage.removeItem('clientType');
        setIsLoading(false);
        return;
      }
      
      if (code) {
        window.history.replaceState({}, document.title, window.location.pathname);
        setTimeout(() => {
          checkAuthStatus();
        }, 1000);
      } else {
        checkAuthStatus();
      }
    };

    handleOAuthCallback();
  }, [amplifyConfigured]);

  useEffect(() => {
    if (storeData?.store_domain) {
      logAnalyticsEvent(analyticsEvents.userLoggedIn)
    }
  }, [storeData?.store_domain])

  // Show loading spinner while checking authentication
  if (isLoading || !amplifyConfigured) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="100vh">
        <Spinner size="xl" color="blue.500" />
      </Box>
    );
  }

    // For public routes, render the public router directly
  if (isPublicRoute()) {
    return (
      <div className= "App" >
        <RouterProvider router={ createPublicRouter() } />
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    clearAuthStorage()
    const currentPath = window.location.pathname + window.location.search + window.location.hash;
    const customStateValue = (currentPath !== '/' && currentPath !== '') ? currentPath : undefined;
    console.log('Signing in with customState:', customStateValue, currentPath);
    
    signInWithRedirect({
      customState: customStateValue,
      options: {
        prompt: 'LOGIN'
      }
    });
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="100vh">
        <Spinner size="xl" color="blue.500" />
      </Box>
    );
  }
  
  const hideTabs = getHideTabs(isStoreAdmin, isStoreAgent, userPermissions);

  const redirectMasterfflProfile = (claimedProfiles: any[]) => {
    const baseUrl = (import.meta.env.VITE_MASTERFFL_PAGE_URL || '').replace(/\/$/, '');

    if (!claimedProfiles?.length) {
      window.location.href = `${baseUrl}/login`;
      return (
        <Box display="flex" alignItems="center" justifyContent="center" minHeight="100vh">
          <Spinner size="xl" color="blue.500" />
        </Box>
      );
    }

    if (claimedProfiles.length === 1) {
      const slug = claimedProfiles[0]?.slug_for_seo?.replace(/^\//, '') || '';
      const redirectUrl = slug ? `${baseUrl}/ffl-dealers/${slug}` : `${baseUrl}/login`;
      window.location.href = redirectUrl;
    } else {
      window.location.href = `${baseUrl}/login`;
    }

    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="100vh">
        <Spinner size="xl" color="blue.500" />
      </Box>
    );
  };

  return (
    <div className="App">
      {isMyFFLRole ?
        // <RouterProvider
        //   router={myFFLRouter(isSuperAdmin, fFLDealerId, userPermissions, claimedProfiles)}
        // />
        redirectMasterfflProfile(claimedProfilesData)
        :
        (isSuperAdmin || isGlobalOperations || isStoreAdmin || isStoreAgent || isSuperAdminRole || isPreferredReportRole || isSearchOnlyAdminRole) && <RouterProvider
          router={router(group, isSuperAdmin, isGlobalOperations, isStoreAdmin, isStoreAgent, isGettingUserInfo, isSuperAdminRole, isPreferredReportRole, isSearchOnlyAdminRole, isImpersonatingRole, hideTabs, userPermissions)}
        />
      }
    </div>
  );
}

export default App;